import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabase";
import TEAMS from "../sport/teams.json";
import { resolveTeam } from "../sport/matchTeams";
import {
  FORMATIONS,
  findCompatibleSlots,
  findDragCompatibleSlots,
} from "../sport/formation";
import { projectSeason, teamStrength, ratingLetter } from "../sport/projection";
import Header from "../components/Header";
import TeamDisplay from "../sport/components/TeamDisplay";
import SoccerField from "../sport/components/Field";
import PlayerCard from "../sport/components/PlayerCard";
import RerollButton from "../components/RerollButton";
import LockInButton from "../components/LockInButton";
import ConfirmDialog from "../components/ConfirmDialog";
import Avatar from "../components/Avatar";
import MobilePitchBar from "../sport/components/MobileLineupBar";
import { glicko2Update } from "../utils/glicko";
import { SquadDropdown } from "../sport/components/SquadList";

const PICK_SECONDS = 25;
const REROLL_LIMIT = 2;
const CLAIM_GRACE_MS = 300000; // 5 minutes of waiting before a win can be claimed
const OPP_INACTIVE_MS = 60000; // opponent considered inactive after this much silence

// Highest-overall player from `team` that fits any open slot (timeout fallback).
function autoPick(team, roster, slots) {
  const rosterNames = new Set(Object.values(roster).map((p) => p.name));
  const candidates = [...team.players]
    .filter((p) => !rosterNames.has(p.name))
    .sort((a, b) => b.overall - a.overall);
  for (const p of candidates) {
    const compat = findCompatibleSlots(p, roster, slots);
    if (compat.length > 0) return { player: p, slot: compat[0] };
  }
  return null;
}

async function computeWinLossParams(matchData, winnerId) {
  const { data: entries } = await supabase
    .from("leaderboard_entries")
    .select("user_id, glicko_rating, glicko_rd, glicko_vol")
    .eq("mode", "ranked")
    .in("user_id", [matchData.player1, matchData.player2]);
  const e1 = entries?.find((e) => e.user_id === matchData.player1);
  const e2 = entries?.find((e) => e.user_id === matchData.player2);
  if (!e1 || !e2) return null;
  const c1 = { rating: e1.glicko_rating, rd: e1.glicko_rd, vol: e1.glicko_vol };
  const c2 = { rating: e2.glicko_rating, rd: e2.glicko_rd, vol: e2.glicko_vol };
  const p1Win = winnerId === matchData.player1;
  const n1 = glicko2Update(c1, c2, p1Win ? 1 : 0);
  const n2 = glicko2Update(c2, c1, p1Win ? 0 : 1);
  return {
    p1_new_rating: n1.rating,
    p1_new_rd: n1.rd,
    p1_new_vol: n1.vol,
    p1_delta: n1.rating - c1.rating,
    p2_new_rating: n2.rating,
    p2_new_rd: n2.rd,
    p2_new_vol: n2.vol,
    p2_delta: n2.rating - c2.rating,
  };
}

async function doForfeit(matchData, userId) {
  const opponentId =
    userId === matchData.player1 ? matchData.player2 : matchData.player1;
  const params = await computeWinLossParams(matchData, opponentId);
  if (!params) return;
  const { error } = await supabase.rpc("forfeit_match", {
    p_match: matchData.id,
    ...params,
  });
  if (error) console.warn("forfeit_match failed:", error);
  try {
    sessionStorage.removeItem(`ranked_started_${matchData.id}`);
  } catch (e) {}
}

async function resolveMatchClient(m) {
  const { data: entries } = await supabase
    .from("leaderboard_entries")
    .select("user_id, glicko_rating, glicko_rd, glicko_vol")
    .eq("mode", "ranked")
    .in("user_id", [m.player1, m.player2]);
  const e1 = entries?.find((e) => e.user_id === m.player1);
  const e2 = entries?.find((e) => e.user_id === m.player2);
  if (!e1 || !e2) return;

  let outcome, s1, s2;
  if (m.p1_points > m.p2_points) {
    outcome = "p1";
    s1 = 1;
    s2 = 0;
  } else if (m.p2_points > m.p1_points) {
    outcome = "p2";
    s1 = 0;
    s2 = 1;
  } else {
    outcome = "tie";
    s1 = 0.5;
    s2 = 0.5;
  }

  const c1 = { rating: e1.glicko_rating, rd: e1.glicko_rd, vol: e1.glicko_vol };
  const c2 = { rating: e2.glicko_rating, rd: e2.glicko_rd, vol: e2.glicko_vol };
  const n1 = glicko2Update(c1, c2, s1);
  const n2 = glicko2Update(c2, c1, s2);

  const { error } = await supabase.rpc("resolve_match", {
    p_match: m.id,
    p_outcome: outcome,
    p1_new_rating: n1.rating,
    p1_new_rd: n1.rd,
    p1_new_vol: n1.vol,
    p1_delta: n1.rating - c1.rating,
    p2_new_rating: n2.rating,
    p2_new_rd: n2.rd,
    p2_new_vol: n2.vol,
    p2_delta: n2.rating - c2.rating,
  });
  if (error) console.warn("resolve_match failed:", error);
}

export default function RankedMatchPage() {
  const { matchId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [match, setMatch] = useState(null);
  const [names, setNames] = useState({});
  const [avatars, setAvatars] = useState({});
  const [phase, setPhase] = useState("loading"); // loading | playing | done | error

  const [roundIndex, setRoundIndex] = useState(0);
  const [currentTeam, setCurrentTeam] = useState(null);
  const [roster, setRoster] = useState({});
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [manualSlot, setManualSlot] = useState(null);
  const [viewedPlayer, setViewedPlayer] = useState(null);
  const [timeLeft, setTimeLeft] = useState(PICK_SECONDS);
  const [rerollsLeft, setRerollsLeft] = useState({
    team: REROLL_LIMIT,
    year: REROLL_LIMIT,
  });
  const [dragging, setDragging] = useState(null);
  const [confirmingLeave, setConfirmingLeave] = useState(false);
  const [canClaim, setCanClaim] = useState(false);
  const [claimCountdown, setClaimCountdown] = useState(null);
  const [finalMatch, setFinalMatch] = useState(null);
  const [myNewRating, setMyNewRating] = useState(null);

  const slots = useMemo(
    () => (match ? FORMATIONS[match.formation] : []),
    [match],
  );

  const compatibleSlots = findCompatibleSlots(selectedPlayer, roster, slots);
  const previewSlot =
    manualSlot && compatibleSlots.includes(manualSlot)
      ? manualSlot
      : compatibleSlots[0] || null;
  const displayPlayer = viewedPlayer || selectedPlayer;
  const dragCompatibleSlots = dragging
    ? findDragCompatibleSlots(
        dragging.player,
        roster,
        slots,
        dragging.sourceSlotId,
      )
    : [];

  // Latest-state ref so the timer's auto-pick reads fresh values
  const latestRef = useRef({});
  latestRef.current = {
    selectedPlayer,
    previewSlot,
    roster,
    currentTeam,
    roundIndex,
  };
  const lockingRef = useRef(false);
  const doTimeoutRef = useRef(() => {});
  const loadedRef = useRef(null);
  const submittedRef = useRef(false);
  const waitStartRef = useRef(null);

  // End early if the opponent forfeits / I get claimed against while playing
  useEffect(() => {
    if (phase !== "playing" || !match || !user) return;
    let cancelled = false;
    const id = setInterval(async () => {
      const { data: m } = await supabase
        .from("matches")
        .select("*")
        .eq("id", match.id)
        .maybeSingle();
      if (cancelled || !m) return;
      if (m.outcome) {
        try {
          sessionStorage.removeItem(`ranked_started_${match.id}`);
        } catch (e) {}
        setFinalMatch(m);
        setPhase("done");
      }
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [phase, match, user]);

  // Heartbeat while actively in the match (playing or waiting unresolved)
  useEffect(() => {
    if (!match || !user) return;
    const active = phase === "playing" || (phase === "done" && !finalMatch);
    if (!active) return;
    const beat = () => supabase.rpc("match_heartbeat", { p_match: match.id });
    beat();
    const id = setInterval(beat, 10000);
    return () => clearInterval(id);
  }, [phase, match, user, finalMatch]);

  // Load match (guarded against StrictMode double-run; handles reload/forfeit)
  useEffect(() => {
    if (!user) return;
    if (loadedRef.current === matchId) return;
    loadedRef.current = matchId;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("matches")
        .select("*")
        .eq("id", matchId)
        .maybeSingle();
      if (cancelled) return;
      if (error || !data) {
        setPhase("error");
        return;
      }
      setMatch(data);
      const { data: profs } = await supabase
        .from("profiles")
        .select("id, username, avatar_url")
        .in("id", [data.player1, data.player2]);
      const map = {};
      const avatarMap = {};
      (profs || []).forEach((p) => {
        map[p.id] = p.username;
        avatarMap[p.id] = p.avatar_url;
      });
      if (cancelled) return;
      setNames(map);
      setAvatars(avatarMap);

      if (data.status === "complete") {
        setFinalMatch(data);
        setPhase("done");
        return;
      }

      const iAmP1 = user.id === data.player1;
      const myRoster = iAmP1 ? data.p1_roster : data.p2_roster;
      if (myRoster) {
        setPhase("done");
        return;
      }

      const key = `ranked_started_${matchId}`;
      let started = false;
      try {
        started = !!sessionStorage.getItem(key);
      } catch (e) {}
      if (started) {
        await doForfeit(data, user.id);
        const { data: m2 } = await supabase
          .from("matches")
          .select("*")
          .eq("id", matchId)
          .maybeSingle();
        if (cancelled) return;
        setFinalMatch(m2 || data);
        setPhase("done");
        return;
      }

      try {
        sessionStorage.setItem(key, "1");
      } catch (e) {}
      setCurrentTeam(resolveTeam(data.teams[0]));
      setRoundIndex(0);
      setPhase("playing");
    })();
    return () => {
      cancelled = true;
    };
  }, [matchId, user]);

  // --- Round progression ---
  const advanceRound = (newRoster) => {
    const nextIndex = latestRef.current.roundIndex + 1;
    setRoster(newRoster);
    setSelectedPlayer(null);
    setManualSlot(null);
    setViewedPlayer(null);
    if (nextIndex >= slots.length) {
      setPhase("done");
      return;
    }
    setRoundIndex(nextIndex);
    setCurrentTeam(resolveTeam(match.teams[nextIndex]));
    lockingRef.current = false;
  };

  const lockPlayer = (player, slot) => {
    if (lockingRef.current) return;
    lockingRef.current = true;
    const { roster: r, currentTeam: ct } = latestRef.current;
    const locked = {
      ...player,
      teamId: ct.id,
      club: ct.club,
      season: ct.season,
      fifaEdition: ct.fifaEdition,
    };
    advanceRound({ ...r, [slot]: locked });
  };

  doTimeoutRef.current = () => {
    if (lockingRef.current) return;
    const {
      selectedPlayer: sp,
      previewSlot: ps,
      roster: r,
      currentTeam: ct,
    } = latestRef.current;
    if (sp && ps) {
      lockPlayer(sp, ps);
    } else if (ct) {
      const auto = autoPick(ct, r, slots);
      if (auto) lockPlayer(auto.player, auto.slot);
      else advanceRound(r);
    }
  };

  // --- Pick timer: restarts each round ---
  useEffect(() => {
    if (phase !== "playing") return;
    setTimeLeft(PICK_SECONDS);
    lockingRef.current = false;
    const id = setInterval(() => {
      setTimeLeft((t) => {
        if (t <= 1) {
          doTimeoutRef.current();
          return PICK_SECONDS;
        }
        return t - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [roundIndex, phase]);

  // --- Leave warning ---
  useEffect(() => {
    if (phase !== "playing") return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [phase]);

  // --- Handlers ---
  const handlePlayerClick = (p) => {
    if (lockingRef.current) return;
    setSelectedPlayer(p);
    setManualSlot(null);
    setViewedPlayer(null);
  };
  const handleClaim = async () => {
    if (!match || !user) return;
    const params = await computeWinLossParams(match, user.id);
    if (!params) return;
    const { data: ok, error } = await supabase.rpc("claim_win", {
      p_match: match.id,
      ...params,
    });
    if (error) {
      console.warn("claim_win failed:", error);
      return;
    }
    if (ok) {
      const { data: m2 } = await supabase
        .from("matches")
        .select("*")
        .eq("id", match.id)
        .maybeSingle();
      if (m2) setFinalMatch(m2);
    } else {
      setCanClaim(false);
    }
  };
  const handleConfirmLeave = async () => {
    setConfirmingLeave(false);
    if (match) await doForfeit(match, user.id);
    navigate("/");
  };
  const handleSlotClick = (slotId) => {
    if (compatibleSlots.includes(slotId)) setManualSlot(slotId);
  };
  const handleLockedSlotClick = (slotId) => {
    const p = roster[slotId];
    if (!p) return;
    setViewedPlayer(p);
    setSelectedPlayer(null);
    setManualSlot(null);
  };
  const handleLockIn = () => {
    if (selectedPlayer && previewSlot) lockPlayer(selectedPlayer, previewSlot);
  };
  // Mobile: tap a bottom-pad slot to place the selected player there
  const handleMobilePlace = (slotId) => {
    if (!selectedPlayer || !compatibleSlots.includes(slotId)) return;
    lockPlayer(selectedPlayer, slotId);
  };

  const sameSeason = currentTeam
    ? TEAMS.filter(
        (t) => t.season === currentTeam.season && t.id !== currentTeam.id,
      )
    : [];
  const sameClub = currentTeam
    ? TEAMS.filter(
        (t) => t.club === currentTeam.club && t.id !== currentTeam.id,
      )
    : [];
  const canRerollTeam = rerollsLeft.team > 0 && sameSeason.length > 0;
  const canRerollYear = rerollsLeft.year > 0 && sameClub.length > 0;

  const handleReroll = (mode) => {
    const pool = mode === "team" ? sameSeason : sameClub;
    if (rerollsLeft[mode] <= 0 || pool.length === 0) return;
    setCurrentTeam(pool[Math.floor(Math.random() * pool.length)]);
    setSelectedPlayer(null);
    setManualSlot(null);
    setViewedPlayer(null);
    setRerollsLeft((r) => ({ ...r, [mode]: r[mode] - 1 }));
  };

  const handleDragStart = (sourceSlotId, isLocked) => {
    if (isLocked) {
      const player = roster[sourceSlotId];
      if (!player) return;
      setDragging({ player, sourceSlotId, isLocked: true });
      setSelectedPlayer(null);
      setManualSlot(null);
      setViewedPlayer(null);
    } else if (selectedPlayer) {
      setDragging({ player: selectedPlayer, sourceSlotId, isLocked: false });
    }
  };
  const handleDragEnd = () => setDragging(null);
  const handleDrop = (targetSlotId) => {
    if (!dragging || !dragCompatibleSlots.includes(targetSlotId)) {
      setDragging(null);
      return;
    }
    if (dragging.isLocked) {
      const targetPlayer = roster[targetSlotId];
      setRoster((r) => {
        const next = { ...r };
        if (targetPlayer) {
          next[dragging.sourceSlotId] = targetPlayer;
          next[targetSlotId] = dragging.player;
        } else {
          delete next[dragging.sourceSlotId];
          next[targetSlotId] = dragging.player;
        }
        return next;
      });
    } else {
      setManualSlot(targetSlotId);
    }
    setDragging(null);
  };

  const result = useMemo(() => {
    if (phase !== "done") return null;
    const season = projectSeason(roster, slots);
    return {
      season,
      strength: teamStrength(roster, slots),
      rating: ratingLetter(season),
    };
  }, [phase, roster, slots]);

  // Poll for opponent + resolve when both in; surface claim if opponent stale
  useEffect(() => {
    if (phase !== "done" || !match || !user) return;
    if (waitStartRef.current === null) waitStartRef.current = Date.now();
    let cancelled = false;
    let resolving = false;
    let intervalId;
    const tick = async () => {
      const { data: m } = await supabase
        .from("matches")
        .select("*")
        .eq("id", match.id)
        .maybeSingle();
      if (cancelled || !m) return;
      if (m.outcome) {
        setFinalMatch(m);
        clearInterval(intervalId);
        return;
      }
      if (m.p1_roster && m.p2_roster && !resolving) {
        resolving = true;
        await resolveMatchClient(m);
        resolving = false;
      }
      const iAmP1 = user.id === m.player1;
      const oppSeen = iAmP1 ? m.p2_last_seen : m.p1_last_seen;
      const oppInactive =
        !oppSeen || new Date(oppSeen).getTime() < Date.now() - OPP_INACTIVE_MS;
      const graceLeft = Math.max(
        0,
        Math.ceil(
          (CLAIM_GRACE_MS - (Date.now() - waitStartRef.current)) / 1000,
        ),
      );
      setCanClaim(oppInactive && graceLeft <= 0);
      setClaimCountdown(oppInactive ? graceLeft : null);
    };
    intervalId = setInterval(tick, 2000);
    tick();
    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [phase, match, user]);

  // Submit my result once when I finish
  useEffect(() => {
    if (phase !== "done" || !match || !user || submittedRef.current) return;
    if (match.status === "complete") return;
    if (Object.keys(roster).length === 0) return;
    submittedRef.current = true;
    const season = projectSeason(roster, slots);
    (async () => {
      const { error } = await supabase.rpc("submit_match_result", {
        p_match: match.id,
        p_roster: roster,
        p_wins: season.wins,
        p_draws: season.draws,
        p_losses: season.losses,
        p_points: season.wins * 3 + season.draws,
        p_rating: ratingLetter(season),
      });
      if (error) console.warn("submit_match_result failed:", error);
      else {
        try {
          sessionStorage.removeItem(`ranked_started_${match.id}`);
        } catch (e) {}
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, match, user]);

  // Fetch my updated rating once resolved
  useEffect(() => {
    if (!finalMatch || !user) return;
    supabase
      .from("leaderboard_entries")
      .select("glicko_rating")
      .eq("user_id", user.id)
      .eq("mode", "ranked")
      .maybeSingle()
      .then(({ data }) => setMyNewRating(data?.glicko_rating ?? null));
  }, [finalMatch, user]);

  // --- Render ---
  if (phase === "loading") {
    return (
      <div className="h-screen flex flex-col">
        <Header />
        <div className="flex-1 grid place-items-center text-zinc-500 animate-pulse">
          Loading match…
        </div>
      </div>
    );
  }
  if (phase === "error") {
    return (
      <div className="h-screen flex flex-col">
        <Header />
        <div className="flex-1 grid place-items-center text-zinc-500">
          Match not found.
        </div>
      </div>
    );
  }

  if (phase === "done") {
    const iAmP1 = user?.id === match.player1;
    const opponentId = iAmP1 ? match.player2 : match.player1;

    if (!finalMatch) {
      return (
        <div className="h-screen flex flex-col">
          <Header />
          <main className="flex-1 grid place-items-center p-6">
            <div className="text-center max-w-md">
              <div className="text-[10px] uppercase tracking-[0.25em] text-emerald-500/80 mb-3">
                Your squad is locked in
              </div>
              {result && Object.keys(roster).length > 0 && (
                <>
                  <div className="flex items-baseline justify-center gap-6 mb-1">
                    <span className="text-5xl font-black text-emerald-400 tabular-nums">
                      {result.season.wins}
                    </span>
                    <span className="text-5xl font-black text-zinc-400 tabular-nums">
                      {result.season.draws}
                    </span>
                    <span className="text-5xl font-black text-red-400 tabular-nums">
                      {result.season.losses}
                    </span>
                  </div>
                  <div className="text-xs text-zinc-500">W · D · L</div>
                  <div className="text-2xl font-bold text-emerald-400 mt-3">
                    {result.rating}
                  </div>
                </>
              )}
              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-zinc-400">
                <Avatar
                  username={names[opponentId]}
                  url={avatars[opponentId]}
                  size={20}
                />
                <span>Waiting for {names[opponentId] || "opponent"}…</span>
                <div className="w-2.5 h-2.5 rounded-full bg-emerald-400 animate-pulse ml-1" />
              </div>

              {canClaim ? (
                <button
                  onClick={handleClaim}
                  className="mt-5 px-5 py-2.5 rounded-full bg-emerald-500 text-zinc-950 font-semibold text-sm hover:bg-emerald-400 transition"
                >
                  Opponent inactive — claim win
                </button>
              ) : claimCountdown != null && claimCountdown > 0 ? (
                <div className="mt-5 text-xs text-zinc-500">
                  Opponent looks inactive — you can claim in {claimCountdown}s
                </div>
              ) : null}
            </div>
          </main>
        </div>
      );
    }

    const forfeitedBy = finalMatch.forfeited_by;
    const iForfeited = forfeitedBy && forfeitedBy === user.id;
    const oppForfeited = forfeitedBy && forfeitedBy !== user.id;

    const my = iAmP1
      ? {
          w: finalMatch.p1_wins,
          d: finalMatch.p1_draws,
          l: finalMatch.p1_losses,
          r: finalMatch.p1_rating,
        }
      : {
          w: finalMatch.p2_wins,
          d: finalMatch.p2_draws,
          l: finalMatch.p2_losses,
          r: finalMatch.p2_rating,
        };
    const opp = iAmP1
      ? {
          w: finalMatch.p2_wins,
          d: finalMatch.p2_draws,
          l: finalMatch.p2_losses,
          r: finalMatch.p2_rating,
        }
      : {
          w: finalMatch.p1_wins,
          d: finalMatch.p1_draws,
          l: finalMatch.p1_losses,
          r: finalMatch.p1_rating,
        };
    const haveRecords = my.w != null && opp.w != null;

    const tie = finalMatch.outcome === "tie";
    const won = !tie && (finalMatch.outcome === "p1") === iAmP1;
    const verdict = tie ? "Draw" : won ? "Victory" : "Defeat";
    const verdictColor = tie
      ? "text-zinc-300"
      : won
        ? "text-emerald-400"
        : "text-red-400";
    const subtitle = iForfeited
      ? "You left the match"
      : oppForfeited
        ? `${names[opponentId] || "Opponent"} left the match`
        : null;

    const myDelta = iAmP1
      ? finalMatch.p1_glicko_delta
      : finalMatch.p2_glicko_delta;
    const newR = myNewRating != null ? Math.round(myNewRating) : null;
    const oldR =
      newR != null && myDelta != null ? Math.round(newR - myDelta) : null;
    const deltaRounded = myDelta != null ? Math.round(myDelta) : null;

    const Side = ({ name, avatarUrl, data, highlight }) => (
      <div
        className={`flex-1 rounded-2xl border p-5 flex flex-col items-center gap-2
          ${highlight ? "bg-emerald-500/5 border-emerald-500/40" : "bg-zinc-900/40 border-zinc-800"}`}
      >
        <Avatar username={name} url={avatarUrl} size={48} />
        <div className="text-sm font-semibold text-zinc-100 truncate max-w-[130px]">
          {name}
        </div>
        <div className="text-xs tabular-nums">
          <span className="text-emerald-400 font-semibold">{data.w}</span>
          <span className="text-zinc-700 mx-0.5">·</span>
          <span className="text-zinc-400">{data.d}</span>
          <span className="text-zinc-700 mx-0.5">·</span>
          <span className="text-red-400">{data.l}</span>
        </div>
        <span className="text-lg font-bold text-emerald-400">{data.r}</span>
      </div>
    );

    return (
      <div className="h-screen flex flex-col">
        <Header />
        <main className="flex-1 grid place-items-center p-6">
          <div className="w-full max-w-xl text-center">
            <div className={`text-4xl font-black ${verdictColor}`}>
              {verdict}
            </div>
            {subtitle && (
              <div className="text-sm text-zinc-500 mt-2">{subtitle}</div>
            )}

            {haveRecords && (
              <div className="flex items-stretch gap-3 mt-8 mb-8">
                <Side
                  name={names[user.id] || "You"}
                  avatarUrl={avatars[user.id]}
                  data={my}
                  highlight
                />
                <div className="grid place-items-center text-zinc-600 text-sm">
                  vs
                </div>
                <Side
                  name={names[opponentId] || "Opponent"}
                  avatarUrl={avatars[opponentId]}
                  data={opp}
                />
              </div>
            )}

            {newR != null && (
              <div
                className={`inline-flex items-center gap-3 bg-zinc-900/60 border border-zinc-800 rounded-full px-5 py-2.5 ${haveRecords ? "mb-8" : "mt-8 mb-8"}`}
              >
                {oldR != null && (
                  <span className="text-zinc-500 tabular-nums">{oldR}</span>
                )}
                <span className="text-zinc-600">→</span>
                <span className="text-zinc-100 font-bold tabular-nums">
                  {newR}
                </span>
                {deltaRounded != null && (
                  <span
                    className={`text-sm font-bold tabular-nums ${deltaRounded >= 0 ? "text-emerald-400" : "text-red-400"}`}
                  >
                    {deltaRounded >= 0 ? `+${deltaRounded}` : deltaRounded} SR
                  </span>
                )}
              </div>
            )}

            {((iAmP1 ? finalMatch.p1_roster : finalMatch.p2_roster) ||
              (iAmP1 ? finalMatch.p2_roster : finalMatch.p1_roster)) && (
              <div className="mb-8 space-y-2 text-left">
                {(iAmP1 ? finalMatch.p1_roster : finalMatch.p2_roster) && (
                  <SquadDropdown
                    label={`${names[user.id] || "You"} — squad`}
                    roster={iAmP1 ? finalMatch.p1_roster : finalMatch.p2_roster}
                    slots={slots}
                  />
                )}
                {(iAmP1 ? finalMatch.p2_roster : finalMatch.p1_roster) && (
                  <SquadDropdown
                    label={`${names[opponentId] || "Opponent"} — squad`}
                    roster={iAmP1 ? finalMatch.p2_roster : finalMatch.p1_roster}
                    slots={slots}
                  />
                )}
              </div>
            )}

            <div className="flex items-center justify-center gap-3">
              <button
                onClick={() => navigate("/leaderboard?mode=ranked")}
                className="px-5 py-2.5 rounded-full bg-zinc-900 border border-zinc-800 text-sm font-semibold text-zinc-200 hover:border-zinc-700 transition"
              >
                Leaderboard
              </button>
              <button
                onClick={() => navigate("/")}
                className="px-6 py-2.5 rounded-full bg-emerald-500 text-zinc-950 font-semibold text-sm hover:bg-emerald-400 transition"
              >
                Back to hub
              </button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  // phase === "playing"
  const timerColor =
    timeLeft <= 5
      ? "text-red-400"
      : timeLeft <= 8
        ? "text-amber-400"
        : "text-zinc-100";

  return (
    <div className="h-screen flex flex-col">
      <Header onLogoClick={() => setConfirmingLeave(true)} />

      <main className="flex-1 min-h-0">
        {/* ===== Desktop (lg+) ===== */}
        <div className="hidden lg:grid h-full p-6 gap-6 grid-cols-[minmax(0,500px)_minmax(0,1fr)_minmax(0,380px)]">
          <TeamDisplay
            key={currentTeam.id}
            team={currentTeam}
            selectedId={selectedPlayer?.id}
            roster={roster}
            slots={slots}
            locked={false}
            onPlayerClick={handlePlayerClick}
          />

          <div className="flex flex-col h-full min-h-0 gap-3">
            <div className="flex items-center gap-3 shrink-0">
              <div className="flex items-center gap-2 shrink-0">
                <RerollButton
                  onClick={() => handleReroll("team")}
                  disabled={!canRerollTeam}
                  label="Team"
                  count={rerollsLeft.team}
                />
                <RerollButton
                  onClick={() => handleReroll("year")}
                  disabled={!canRerollYear}
                  label="Year"
                  count={rerollsLeft.year}
                />
              </div>

              <div className="flex-1" />

              <div className="flex items-center gap-3 shrink-0">
                <div
                  className={`text-3xl font-black tabular-nums ${timerColor} transition-colors`}
                >
                  {timeLeft}s
                </div>
                <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                  Round {roundIndex + 1}/{slots.length}
                </span>
              </div>
            </div>

            <div className="flex-1 min-h-0 relative">
              <SoccerField
                slots={slots}
                roster={roster}
                previewSlot={previewSlot}
                previewPlayer={selectedPlayer}
                compatibleSlots={compatibleSlots}
                onSlotClick={handleSlotClick}
                viewedPlayer={viewedPlayer}
                onLockedClick={handleLockedSlotClick}
                dragging={dragging}
                dragCompatibleSlots={dragCompatibleSlots}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
              />

              {selectedPlayer && previewSlot && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10">
                  <div className="animate-scale-in">
                    <LockInButton locked={false} onClick={handleLockIn} />
                  </div>
                </div>
              )}
            </div>
          </div>

          <PlayerCard player={displayPlayer} />
        </div>

        {/* ===== Mobile (below lg) ===== */}
        <div className="lg:hidden h-full flex flex-col min-h-0">
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-900 shrink-0">
            <div className="flex items-center gap-2">
              <RerollButton
                onClick={() => handleReroll("team")}
                disabled={!canRerollTeam}
                label="Team"
                count={rerollsLeft.team}
              />
              <RerollButton
                onClick={() => handleReroll("year")}
                disabled={!canRerollYear}
                label="Year"
                count={rerollsLeft.year}
              />
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-3 shrink-0">
              <div
                className={`text-2xl font-black tabular-nums ${timerColor} transition-colors`}
              >
                {timeLeft}s
              </div>
              <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                {roundIndex + 1}/{slots.length}
              </span>
            </div>
          </div>

          <div className="flex-1 min-h-0 px-3 pt-3">
            <TeamDisplay
              key={`m-${currentTeam.id}`}
              team={currentTeam}
              selectedId={selectedPlayer?.id}
              roster={roster}
              slots={slots}
              locked={false}
              onPlayerClick={handlePlayerClick}
            />
          </div>

          <MobilePitchBar
            slots={slots}
            roster={roster}
            compatibleSlots={compatibleSlots}
            selectedPlayer={selectedPlayer}
            onPlace={handleMobilePlace}
          />
        </div>
      </main>

      <ConfirmDialog
        open={confirmingLeave}
        title="Leave the match?"
        description="Leaving counts as a loss and your opponent gets the win."
        confirmLabel="Forfeit"
        destructive
        onConfirm={handleConfirmLeave}
        onCancel={() => setConfirmingLeave(false)}
      />
    </div>
  );
}
